
import { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { AssessmentsHeader } from "./components/AssessmentsHeader";
import { useAuth } from "@/components/AuthProvider";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";

export const StudentAssessmentsList = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslations("assessments");

  const { data: assessments, isLoading } = useQuery({
    queryKey: ["student-assessments", user?.email],
    queryFn: async () => {
      if (!user?.email) return [];
      
      // First get the student ID
      const { data: student, error: studentError } = await supabase
        .from("students")
        .select("id")
        .eq("email", user.email)
        .single();
        
      if (studentError) {
        console.error("Error fetching student:", studentError);
        return [];
      }
      
      // Then get the assessments
      const { data, error } = await supabase
        .from("physical_assessments")
        .select(`
          *,
          trainer:profiles(first_name, last_name)
        `)
        .eq("student_id", student.id)
        .order("assessment_date", { ascending: false });
        
      if (error) {
        console.error("Error fetching assessments:", error);
        return [];
      }
      
      return data;
    },
    enabled: !!user?.email
  });

  const filteredAssessments = assessments?.filter(assessment => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const dateLocale = language === 'pt' ? ptBR : enUS;
    const date = format(new Date(assessment.assessment_date), "dd/MM/yyyy", { locale: dateLocale });
    
    return (
      date.includes(searchLower) ||
      (assessment.observations && assessment.observations.toLowerCase().includes(searchLower))
    );
  });

  const dateLocale = language === 'pt' ? ptBR : enUS;

  return (
    <div className="space-y-6">
      <AssessmentsHeader />

      <div className="relative flex-1 w-full sm:max-w-xs">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground dark:text-gray-400" />
        <Input
          placeholder={t("list.search")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8 bg-white/90 border border-slate-100 rounded-xl shadow-sm focus:ring-1 focus:ring-flame/20 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder:text-gray-400"
        />
      </div>

      {isLoading ? (
        <div className="bg-white/90 backdrop-blur-lg rounded-xl shadow-md border border-slate-100/50 p-6 dark:bg-gray-800 dark:border-gray-700">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-slate-200 rounded w-1/4 dark:bg-gray-700"></div>
            <div className="h-10 bg-slate-200 rounded dark:bg-gray-700"></div>
            <div className="h-10 bg-slate-200 rounded dark:bg-gray-700"></div>
          </div>
        </div>
      ) : filteredAssessments && filteredAssessments.length > 0 ? (
        <div className="bg-white/90 backdrop-blur-lg rounded-xl shadow-md border border-slate-100/50 overflow-hidden hover:shadow-lg transition-all dark:bg-gray-800 dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50/50 dark:bg-gray-700/50 dark:border-gray-600">
                  <th className="px-4 py-3 text-left font-medium dark:text-gray-200">{t("list.columns.date")}</th>
                  <th className="px-4 py-3 text-left font-medium dark:text-gray-200">{t("form.weight")}</th>
                  <th className="px-4 py-3 text-left font-medium dark:text-gray-200">{t("form.bodyFatPercentage")}</th>
                  <th className="px-4 py-3 text-left font-medium dark:text-gray-200">{t("form.muscleMass")}</th>
                  <th className="px-4 py-3 text-left font-medium dark:text-gray-200">Trainer</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssessments.map((assessment) => (
                  <tr key={assessment.id} className="border-b hover:bg-slate-50/50 dark:border-gray-600 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 dark:text-gray-300">
                      {format(new Date(assessment.assessment_date), "dd/MM/yyyy", { locale: dateLocale })}
                    </td>
                    <td className="px-4 py-3 dark:text-gray-300">
                      {assessment.weight ? `${assessment.weight} kg` : "-"}
                    </td>
                    <td className="px-4 py-3 dark:text-gray-300">
                      {assessment.body_fat_percentage ? `${assessment.body_fat_percentage}%` : "-"}
                    </td>
                    <td className="px-4 py-3 dark:text-gray-300">
                      {assessment.muscle_mass ? `${assessment.muscle_mass} kg` : "-"}
                    </td>
                    <td className="px-4 py-3 dark:text-gray-300">
                      {assessment.trainer ? `${assessment.trainer.first_name} ${assessment.trainer.last_name}` : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white/90 backdrop-blur-lg rounded-xl shadow-md border border-slate-100/50 overflow-hidden hover:shadow-lg transition-all dark:bg-gray-800 dark:border-gray-700">
          <Card className="border-0 shadow-none dark:bg-transparent">
            <CardHeader>
              <CardTitle className="dark:text-white">{t("title")}</CardTitle>
              <CardDescription className="dark:text-gray-300">
                {language === 'pt' 
                  ? "Você ainda não tem nenhuma avaliação física registada."
                  : "You don't have any physical assessments registered yet."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground dark:text-gray-400">
                {language === 'pt'
                  ? "Contacte seu personal trainer para agendar uma avaliação física."
                  : "Contact your personal trainer to schedule a physical assessment."}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
